const axios = require("axios");
const config = require("../config");

class Report {
    static async request(url, params = {}, signal) {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                query.append(key, value);
            }
        });

        const response = await fetch(`${url}?${query.toString()}`, {
            credentials: "include",
            headers: { Accept: "application/json" },
            signal,
        });
        if (!response.ok) {
            throw new Error(`Dashboard request failed with ${response.status}`);
        }

        return response.json();
    }

    static async manifest(url, params, signal) {
        const response = await this.request(
            url,
            { ...params, manifest: "1" },
            signal,
        );
        return response.data;
    }

    static async batch(url, params, reportBatch, signal) {
        const response = await this.request(
            url,
            { ...params, reportBatch },
            signal,
        );
        return response.data.reports;
    }

    static async stream(url, params = {}, onEvent, signal) {
        const query = new URLSearchParams();
        Object.entries({ ...params, stream: "1" }).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                query.append(key, value);
            }
        });

        const response = await fetch(`${url}?${query.toString()}`, {
            credentials: "include",
            headers: { Accept: "application/x-ndjson" },
            signal,
        });
        if (!response.ok) {
            throw new Error(`Dashboard request failed with ${response.status}`);
        }
        if (!response.body || !response.body.getReader) {
            throw new Error(
                "Streaming responses are not supported by this browser",
            );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffered = "";
        const processLines = (final = false) => {
            const lines = buffered.split("\n");
            buffered = final ? "" : lines.pop();
            lines.forEach((line) => {
                if (line.trim()) {
                    onEvent(JSON.parse(line));
                }
            });
        };

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                buffered += decoder.decode();
                processLines(true);
                return;
            }

            buffered += decoder.decode(value, { stream: true });
            processLines();
        }
    }

    static async retry(url, params, reportBatch) {
        const { data } = await axios.get(url, {
            params: { ...params, reportBatch },
        });
        return data.data.reports;
    }

    static async dashboard(scope, params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/dashboard/" + scope,
            { params },
        );
        return data.data;
    }

    static dashboardStream(scope, params, onEvent, signal) {
        return this.stream(
            config.api_url + "reports/dashboard/" + scope,
            params,
            onEvent,
            signal,
        );
    }

    static dashboardManifest(scope, params, signal) {
        return this.manifest(
            config.api_url + "reports/dashboard/" + scope,
            params,
            signal,
        );
    }

    static dashboardBatch(scope, params, reportBatch, signal) {
        return this.batch(
            config.api_url + "reports/dashboard/" + scope,
            params,
            reportBatch,
            signal,
        );
    }

    static retryDashboard(scope, params, reportBatch) {
        return this.retry(
            config.api_url + "reports/dashboard/" + scope,
            params,
            reportBatch,
        );
    }

    static async entityDashboard(id, params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/entities/" + id + "/dashboard",
            { params },
        );
        return data.data;
    }

    static entityDashboardStream(id, params, onEvent, signal) {
        return this.stream(
            config.api_url + "reports/entities/" + id + "/dashboard",
            params,
            onEvent,
            signal,
        );
    }

    static entityDashboardManifest(id, params, signal) {
        return this.manifest(
            config.api_url + "reports/entities/" + id + "/dashboard",
            params,
            signal,
        );
    }

    static entityDashboardBatch(id, params, reportBatch, signal) {
        return this.batch(
            config.api_url + "reports/entities/" + id + "/dashboard",
            params,
            reportBatch,
            signal,
        );
    }

    static retryEntityDashboard(id, params, reportBatch) {
        return this.retry(
            config.api_url + "reports/entities/" + id + "/dashboard",
            params,
            reportBatch,
        );
    }

    static async memberDashboard(id, params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/members/" + id + "/dashboard",
            { params },
        );
        return data.data;
    }

    static memberDashboardStream(id, params, onEvent, signal) {
        return this.stream(
            config.api_url + "reports/members/" + id + "/dashboard",
            params,
            onEvent,
            signal,
        );
    }

    static memberDashboardManifest(id, params, signal) {
        return this.manifest(
            config.api_url + "reports/members/" + id + "/dashboard",
            params,
            signal,
        );
    }

    static memberDashboardBatch(id, params, reportBatch, signal) {
        return this.batch(
            config.api_url + "reports/members/" + id + "/dashboard",
            params,
            reportBatch,
            signal,
        );
    }

    static retryMemberDashboard(id, params, reportBatch) {
        return this.retry(
            config.api_url + "reports/members/" + id + "/dashboard",
            params,
            reportBatch,
        );
    }

    static async memberCredit(id) {
        const { data } = await axios.get(
            config.api_url + "reports/members/credit",
        );
        return data.data;
    }

    static async membershipTeeTimeRequestEngagement(params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/members/tee-time-request-engagement",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async teeTimePaymentMethodMix(params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/members/tee-time-payment-method-mix",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async appUsageOverview(id) {
        const { data } = await axios.get(
            config.api_url + "reports/app-usage/overview",
        );
        return data.data;
    }

    static async GFCSpendByMonth() {
        const { data } = await axios.get(
            config.api_url + "reports/finance/gfc-spend-by-month",
        );
        return data.data;
    }

    static async conciergeTeeTimeBookingOutcomes(params = {}) {
        const { data } = await axios.get(
            config.api_url +
                "reports/concierge/tee-time-booking-outcomes",
            { params },
        );
        return data.data;
    }

    static async teeTimeRequestBookingsByHour(params = {}) {
        const { data } = await axios.get(
            config.api_url +
                "reports/tee-time-requests/bookings-by-hour",
            { params },
        );
        return data.data;
    }

    static async golfClubTeeTimeCompletionRate(params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/golf-clubs/tee-time-completion-rate",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async golfClubTeeTimeRevenue(params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/golf-clubs/tee-time-revenue",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async golfClubTeeTimeBookingOutcomeMix(params = {}) {
        const { data } = await axios.get(
            config.api_url +
                "reports/golf-clubs/tee-time-booking-outcome-mix",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async entityTeeTimeRequestEngagement(params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/entities/tee-time-request-engagement",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async entityCompletedTeeTimeBookingEngagement(params = {}) {
        const { data } = await axios.get(
            config.api_url +
                "reports/entities/completed-tee-time-booking-engagement",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }

    static async entityBookingActivityThreshold(params = {}) {
        const { data } = await axios.get(
            config.api_url + "reports/entities/booking-activity-threshold",
            { params },
        );
        return {
            items: data.data,
            total: data.total,
        };
    }
}

export default Report;
